import React, { useState, useEffect, useMemo, ReactElement, useRef } from 'react';
import { ProductVariant, ProductPriceAmount, InsuranceOffer, LinkedAddonServicesContent } from 'types/Product';
import { Feature, InsuranceContent } from 'types/ProductResponseType';
import findSkuByOption, { findSkuByOptionList } from 'utilities/sku/findSkuByOption';
import filterSKU from 'utilities/sku/filterSKU';
import { addApmLabels, isNotInStock } from 'utilities/util';
import {
  validatePersonalization,
  getPersonalizationErrorMessage,
} from 'utilities/personalization/personalization-utilities';
import scrollElementIntoView from '@sd-utility/scrollelementintoview';
import personalizationErrorList from 'utilities/personalization/personalization-error-list';
import sendCelebrusEvent from 'services/celebrus/celebrus';
import sendAnalyticEvent from 'services/analytic/analytic-service';
import appWebhook from '@sd-utility/appwebhook';
import eventBus from '@sd-utility/eventbus';
import LoggerFactory from 'utilities/loggerv2/loggerFactory';
import ErrorCodes from 'utilities/loggerv2/errorCodes';
import { endSpan, failSpan, startSpan } from 'utilities/loggerv2/performanceSpans';
import BasketServiceFactory, { BasketServiceType } from 'services/basket/factory/BasketServiceFactory';
import { BasketAmendItemParams, BasketItem, BasketResponse } from 'types/BasketTypes';
import { getAmendBasketParams, isAmendBasketEnabled } from 'utilities/basket/basketUtilities';
import { DEFAULT_BASKET_ERROR_MESSAGE } from 'constants/basket';
import { MockDirectives } from 'types/Mock';
import { SelectedSkuContext } from './SelectedSkuContext';

interface SelectedSkuProviderInterface {
  skus: ProductVariant[];
  setSkus: (skus: ProductVariant[]) => void;
  children?: ReactElement | ReactElement[];
  isInsuranceAvailable: boolean;
  productId: string;
  productItemNumber: string;
  productName: string;
  productPrice: ProductPriceAmount;
  digitalDownload: boolean;
  personalizableMaxLength?: number;
  insuranceContent?: InsuranceContent[];
  linkedAddonServicesContent?: LinkedAddonServicesContent[];
  basketMockEnabled: boolean;
  mockDirectives: MockDirectives;
  features?: Feature[];
}

interface SelectedOptions {
  [key: string]: string;
}

const loggerATB = LoggerFactory.getLogger('Add to basket');

function SelectedSkuProvider({
  skus,
  setSkus,
  children,
  isInsuranceAvailable,
  productId,
  productItemNumber,
  productName,
  productPrice,
  digitalDownload,
  personalizableMaxLength,
  insuranceContent,
  linkedAddonServicesContent,
  basketMockEnabled,
  mockDirectives,
  features,
}: SelectedSkuProviderInterface) {
  const defaultSelectedSku = skus.length > 1 ? undefined : skus[0];
  const [selectedSku, setSelectedSku] = useState(defaultSelectedSku);
  const [selectedInsurance, setSelectedInsurance] = useState<string | null | InsuranceOffer>(null);
  const [isAddOnSelected, setIsAddOnSelected] = useState<boolean>(false);
  const [optionSelectorErrorMessage, setOptionSelectorErrorMessage] = useState({});
  const [isOptionSelectorError, setOptionSelectorError] = useState({ value: false }); // Using an object allows us to force a re-render when the value changes
  const [selectedOptions, setSelectedOption] = useState<SelectedOptions>({});
  const [personalizationInput, setPersonalizationInput] = useState<HTMLInputElement>();
  const [personalizationErrorMessage, setPersonalizationErrorMessage] = useState('');
  const uniqueSKUList = useMemo(
    () => (skus[0]?.options && filterSKU(skus, selectedOptions)) || [],
    [selectedOptions, skus],
  );
  const optionsLength = skus.length > 1 ? Object.entries(uniqueSKUList).length : 0;
  const [showCheckItsRightDialog, setCheckItsRightDialog] = useState(false);
  const [isDigitalDownloadDialogOpen, setIsDigitalDownloadDialogOpen] = useState(false);
  const [isAddToBasketDialogRendered, setIsAddToBasketDialogRendered] = useState(false);
  const [isAddToBasketDialogOpen, setIsAddToBasketDialogOpen] = useState(false);
  const [isDigitalDownloadDialogRendered, setIsDigitalDownloadDialogRendered] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const errorContainerRef = useRef();
  const recsDialogRef = useRef<HTMLDivElement>(null);
  const recsAtbRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);
  const [insuranceError, setInsuranceError] = useState(false);
  const [isCheckItsRightDialogRendered, setIsCheckItsRightDialogRendered] = useState(false);
  const [selectedSkuInsuranceContent, setSelectedSkuInsuranceContent] = useState<InsuranceContent | undefined>(
    undefined,
  );
  const [showFindOutMore, setFindOutMore] = useState(false);
  const [filteredLinkedAddonServicesContent, setfilteredLinkedAddonServicesContent] = useState<
    LinkedAddonServicesContent[] | undefined
  >(undefined);

  const [amendBasketParams, setAmendBasketParams] = useState<BasketAmendItemParams>({
    amendItemId: null,
    variantId: null,
  });

  const [amendBasketEnabled, setAmendBasketEnabled] = useState(false);
  const [isAddingToBasket, setIsAddingToBasket] = useState(false);

  const basketService = useMemo(
    () =>
      BasketServiceFactory.createBasketService(
        basketMockEnabled ? BasketServiceType.MOCK : BasketServiceType.ODM,
        mockDirectives,
      ),
    [],
  );

  useEffect(() => {
    const params = getAmendBasketParams();

    setAmendBasketParams(params);
    setAmendBasketEnabled(isAmendBasketEnabled(params));
  }, []);

  const refreshSelectedSku = () => {
    setSelectedSku((previous) => {
      if (previous) {
        const updatedSku = skus.find((sku) => sku.option === previous.option);

        if (updatedSku) {
          return updatedSku;
        }
      }

      return previous;
    });
  };

  useEffect(refreshSelectedSku, [skus]);

  const getInsuranceContent = (sku: ProductVariant) => {
    const insuranceType = insuranceContent?.find(
      (item) => sku?.insuranceOffers && item.id === sku?.insuranceOffers[0].offer.productContentId.toString(),
    );
    return insuranceType;
  };

  const getValidLinkedAddonServices = (sku: ProductVariant) => {
    const validLinkedAddonServices = linkedAddonServicesContent?.filter((service) =>
      sku?.linkedAddOnService?.includes(service.productId),
    );

    return validLinkedAddonServices;
  };

  const handleLinkedAddonServiceSelectionChange = (index: number, state?: boolean) => {
    setfilteredLinkedAddonServicesContent((prevServices) => {
      if (!prevServices) return prevServices;
      const newServices = [...prevServices];
      const isSelected = state ?? !newServices[index].isSelected;
      newServices[index].isSelected = isSelected;
      setIsAddOnSelected(isSelected);
      return newServices;
    });
  };

  const handleSkuPreselect = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const variantParam = amendBasketParams.variantId || searchParams.get('variant');
    const preSelectedSku = variantParam ? findSkuByOption(variantParam, skus) : undefined;
    const hasColourFeature = features?.some((feature) => feature.name === 'colour') ?? true;

    if (!preSelectedSku) return;

    if (!hasColourFeature && preSelectedSku?.options?.colour) {
      delete preSelectedSku.options.colour;
    }

    if (preSelectedSku.options) {
      setSelectedOption(preSelectedSku.options);
    }

    setSelectedSku(preSelectedSku);

    if (isInsuranceAvailable) {
      setSelectedSkuInsuranceContent(getInsuranceContent(preSelectedSku));
    }

    const addon = getValidLinkedAddonServices(preSelectedSku);

    setfilteredLinkedAddonServicesContent(addon);
  };

  useEffect(handleSkuPreselect, [amendBasketParams]);

  const handleOptionSelect = (option: string, optionType: string) => {
    setSelectedOption((prevSelectedOptions: SelectedOptions) => {
      const isOptionAlreadySelected = prevSelectedOptions[`${optionType}`] === option;

      const updatedSelectedOptions = {
        ...prevSelectedOptions,
        [optionType]: option,
      };

      if (isOptionAlreadySelected) {
        delete updatedSelectedOptions[`${optionType}`];
      }

      return updatedSelectedOptions;
    });

    if (insuranceError) {
      setInsuranceError(false);
    }
  };

  useEffect(() => {
    const sku = findSkuByOptionList(skus, selectedOptions);

    if (Object.keys(selectedOptions).length < optionsLength || !sku) {
      setSelectedSku(undefined);
      return;
    }

    setSelectedSku(sku);

    if (isInsuranceAvailable) {
      setSelectedSkuInsuranceContent(getInsuranceContent(sku));
    }

    const addon = getValidLinkedAddonServices(sku);

    setfilteredLinkedAddonServicesContent(addon);
  }, [selectedOptions]);

  useEffect(() => {
    if (selectedInsurance && typeof selectedInsurance !== 'string' && selectedSku && selectedSku?.insuranceOffers) {
      const sameInsuranceOffer = selectedSku?.insuranceOffers?.find(
        ({ offer }) =>
          offer.productId === selectedInsurance?.offer.productId &&
          offer.sellingPrice === selectedInsurance?.offer.sellingPrice,
      );
      if (!sameInsuranceOffer) {
        setSelectedInsurance(null);
      } else {
        setSelectedInsurance(sameInsuranceOffer);
      }
    }
  }, [selectedSku]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (isAddToBasketDialogRendered) {
      setIsAddToBasketDialogOpen(true);
    }
  }, [isAddToBasketDialogRendered]);

  useEffect(() => {
    if (selectedInsurance) {
      setInsuranceError(false);
    }
  }, [selectedInsurance]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (isDigitalDownloadDialogRendered) {
      setIsDigitalDownloadDialogOpen(true);
    }
  }, [isDigitalDownloadDialogRendered]);

  useEffect(() => {
    setIsCheckItsRightDialogRendered(true);
  }, []);

  useEffect(() => {
    if (defaultSelectedSku) {
      setSelectedSkuInsuranceContent(getInsuranceContent(defaultSelectedSku));
    }
  }, []);

  // DOM LOGIC
  const removeElAndAppendChildToDialog = (ref: HTMLDivElement) => {
    ref.removeAttribute('hidden');
    const messageElement = ref.querySelector('[ class*="persuasive-message" ]');
    if (messageElement) messageElement.remove();
    recsDialogRef.current?.appendChild(ref);
  };

  const removeRecsOnDialogClose = (ref: HTMLDivElement) => {
    ref.remove();
  };

  // ERRORS LOGIC
  const handleError = async (message: string) => {
    (await loggerATB).logWithCode(ErrorCodes.BASKET_ADD_ITEM_FAILED, new Error(message));

    setErrorMessage(message);
    setHasError(true);
    scrollElementIntoView(errorContainerRef.current);

    sendCelebrusEvent({
      action: 'failedAdd',
      productID: productId,
      errorMessage: message,
    });
  };

  const handleOptionError = () => {
    setOptionSelectorError({ value: true });
    scrollElementIntoView(document.querySelector('[id*="option-selector-label"]'));
  };

  const handlePersonalizationError = () => {
    if (personalizationInput) {
      const errorType =
        personalizableMaxLength && validatePersonalization(personalizationInput?.value, personalizableMaxLength);
      if (errorType !== personalizationErrorList.type.noError) {
        if (!selectedSku || isNotInStock(selectedSku?.stock?.code)) {
          scrollElementIntoView(personalizationInput);
        }
        return getPersonalizationErrorMessage(errorType as keyof typeof personalizationErrorList.list);
      }
    }
    return null;
  };

  const resetError = () => {
    setErrorMessage('');
    setHasError(false);
    setPersonalizationErrorMessage('');
  };

  const handleInsuranceError = () => {
    setInsuranceError(true);
    scrollElementIntoView(document.querySelector('[id*="insurance-container"]'));
  };

  // BASKET LOGIC
  const getDecimalPrice = () =>
    selectedSku?.price?.amount.decimal ? selectedSku.price.amount.decimal : productPrice.decimal;

  const getUnitSalePrice = (): number =>
    selectedSku?.price?.amount.unformattedPrevious ||
    productPrice.unformattedSaving ||
    productPrice.unformattedSavingTo ||
    productPrice.decimal;

  const triggerAddToBasketAppWebhook = (webhookMethodName: 'addedToBasket' | 'addToBasket'): boolean => {
    const { atbRecs } = window as any;

    const payload: any = {
      currencyCode: productPrice.currencyCode,
      productData: [
        {
          SKU: selectedSku?.option,
          catalogueID: productItemNumber,
          name: productName,
          quantity: 1,
          unitPrice: productPrice.decimal,
        },
      ],
    };

    if (atbRecs) {
      payload.recommendedProducts = { title: 'You may also like', products: atbRecs };
    }

    return appWebhook(webhookMethodName, payload);
  };

  const handleAddToBasketAppWebhooks = (): boolean => {
    let addToBasketAppWebhookResponse = triggerAddToBasketAppWebhook('addedToBasket');

    // Trigger the legacy version of the add to basket app webhook if the above method responds with false
    if (!addToBasketAppWebhookResponse) {
      addToBasketAppWebhookResponse = triggerAddToBasketAppWebhook('addToBasket');
    }

    return addToBasketAppWebhookResponse;
  };

  const handleAddToBasketSuccess = () => {
    resetError();

    if (amendBasketEnabled) {
      window.location.href = '/basket';
    } else if (!handleAddToBasketAppWebhooks()) {
      const recs = recsAtbRef.current;

      if (isAddToBasketDialogRendered) {
        setIsAddToBasketDialogOpen(true);
      } else {
        setIsAddToBasketDialogRendered(true);
      }

      if (recs) {
        removeElAndAppendChildToDialog(recs);
      }
    }
  };

  const handleAddToBasketError = (response: BasketResponse) => {
    if (
      response.message === personalizationErrorList.list.length ||
      response.message === personalizationErrorList.list.symbol
    ) {
      setPersonalizationErrorMessage(response.message);
    } else {
      handleError(response.message);
    }
  };

  const handleAddToBasket = async (buttonLabel = 'Add to basket') => {
    if (isAddingToBasket) {
      return;
    }

    setIsAddingToBasket(true);

    addApmLabels({
      buttonLabel,
      productId,
      skuId: selectedSku?.itemNumber,
    });

    const spanId = startSpan('handleAddToBasket', {
      buttonLabel,
      productId,
      sku: selectedSku?.option,
    });

    const personalizationError = handlePersonalizationError();

    if (
      !selectedSku ||
      isNotInStock(selectedSku?.stock?.code) ||
      personalizationError ||
      (isInsuranceAvailable && selectedSku?.insuranceOffers && !selectedInsurance)
    ) {
      if (personalizationError) {
        setPersonalizationErrorMessage(personalizationError);
      }

      if (!selectedSku || (isNotInStock(selectedSku?.stock?.code) && skus.length > 1)) {
        handleOptionError();
      }

      if (isNotInStock(selectedSku?.stock?.code) && skus.length === 1) {
        const code = selectedSku?.stock?.code;

        if (code === 'OUTOFSTOCK') {
          handleError('Sorry, this item is out of stock');
        }

        if (code === 'UNAVAILABLE') {
          handleError('Sorry, this item is currently unavailable');
        }
      }

      if (!selectedInsurance && selectedSku) {
        handleInsuranceError();
      }

      setIsAddingToBasket(false);
      return;
    }

    if (showCheckItsRightDialog) {
      setCheckItsRightDialog(false);
    }

    try {
      sendAnalyticEvent({
        gaLabel: buttonLabel,
      });

      const getInsuranceSku = () => {
        if (typeof selectedInsurance !== 'string' && !Object.is(selectedInsurance, null)) {
          return `FSS${selectedInsurance?.offer?.productId}-${selectedInsurance?.offer?.sku}`;
        }

        return undefined;
      };

      const addons = filteredLinkedAddonServicesContent
        ?.filter((x) => x.isSelected === true)
        .map((x) => x.optionNumber);

      const basketItem: BasketItem = {
        variantId: selectedSku.option,
        personalisation: personalizationInput?.value,
        selectedAddOnServices: addons,
        warrantyOptionNumber: getInsuranceSku(),
      };

      const basketResponse = isAmendBasketEnabled(amendBasketParams)
        ? await basketService.amendItem({
            lineItem: amendBasketParams.amendItemId,
            item: basketItem,
          })
        : await basketService.addItem({
            item: basketItem,
          });

      if (basketResponse.success) {
        eventBus.publish('basket/add', {
          id: productId,
          sku: selectedSku.option,
          unit_price: getDecimalPrice(),
          unit_sale_price: getUnitSalePrice(),
          quantity: 1,
        });

        sendCelebrusEvent({
          action: 'add',
          currency: productPrice.currencyCode,
          productDisplayName: productName,
          productID: selectedSku.itemNumber,
          quantity: 1,
          skuNumber: selectedSku.itemNumber,
          targetContainer: 'basket',
          value: getDecimalPrice(),
          valuePerItem: getDecimalPrice(),
        });

        handleAddToBasketSuccess();

        endSpan(spanId, {
          success: true,
        });
      } else {
        handleAddToBasketError(basketResponse);

        failSpan(spanId, {
          errorType: 'SERVICE_RESPONSE_ERROR',
          serviceResponse: basketResponse,
        });
      }
    } catch (error) {
      (await loggerATB).logWithCode(ErrorCodes.BASKET_SERVICE_ERROR, error);
      handleError(DEFAULT_BASKET_ERROR_MESSAGE);
    } finally {
      setIsAddingToBasket(false);
    }
  };

  const closeBasketDialog = () => {
    const recs = recsDialogRef.current;
    setIsAddToBasketDialogOpen(false);
    if (recs) removeRecsOnDialogClose(recs);
    sendAnalyticEvent({
      gaLabel: 'Add to Basket Modal - Close',
    });
  };

  const handleContinueShopping = async () => {
    sendAnalyticEvent({
      gaLabel: 'Add to Basket Modal - Continue Shopping',
    });
    const recs = recsDialogRef.current;
    setIsAddToBasketDialogOpen(false);
    if (recs) removeRecsOnDialogClose(recs);
  };

  const handleGoToBasket = async () => {
    sendAnalyticEvent({
      gaLabel: 'Add to Basket Modal - Go to Basket',
    });
    setIsAddToBasketDialogOpen(false);
  };

  const handleDigitalDownloadAddToBasket = () => {
    setIsDigitalDownloadDialogOpen(false);
    handleAddToBasket();
  };

  const handleDigitalDownloadDialogueRender = () => {
    if (isDigitalDownloadDialogRendered) {
      setIsDigitalDownloadDialogOpen(true);
    } else {
      setIsDigitalDownloadDialogRendered(true);
    }
  };

  return (
    <SelectedSkuContext.Provider
      value={useMemo(
        () => ({
          selectedSku,
          filteredLinkedAddonServicesContent,
          optionSelectorErrorMessage,
          isOptionSelectorError,
          uniqueSKUList,
          selectedOptions,
          personalizationErrorMessage,
          personalizationInput,
          skus,
          isAddOnSelected,
          selectedInsurance,
          isInsuranceAvailable,
          showCheckItsRightDialog,
          isDigitalDownloadDialogOpen,
          isAddToBasketDialogRendered,
          isAddToBasketDialogOpen,
          productId,
          productItemNumber,
          productName,
          productPrice,
          digitalDownload,
          personalizableMaxLength,
          hasError,
          errorMessage,
          recsDialogRef,
          recsAtbRef,
          isFirstRender,
          isDigitalDownloadDialogRendered,
          insuranceError,
          isCheckItsRightDialogRendered,
          selectedSkuInsuranceContent,
          showFindOutMore,
          amendBasketEnabled,
          isAddingToBasket,
          setFindOutMore,
          setSelectedSku,
          closeBasketDialog,
          handleDigitalDownloadAddToBasket,
          handleDigitalDownloadDialogueRender,
          setOptionSelectorErrorMessage,
          setOptionSelectorError,
          handleOptionSelect,
          setPersonalizationErrorMessage,
          setPersonalizationInput,
          setSelectedInsurance,
          setCheckItsRightDialog,
          setIsDigitalDownloadDialogOpen,
          handleAddToBasket,
          setHasError,
          handleContinueShopping,
          handleGoToBasket,
          setInsuranceError,
          handleLinkedAddonServiceSelectionChange,
          setSkus,
        }),
        [
          skus,
          productId,
          productItemNumber,
          productName,
          productPrice,
          digitalDownload,
          personalizableMaxLength,
          isInsuranceAvailable,
          showFindOutMore,
          selectedSkuInsuranceContent,
          isCheckItsRightDialogRendered,
          insuranceError,
          selectedSku,
          optionSelectorErrorMessage,
          isOptionSelectorError,
          uniqueSKUList,
          selectedOptions,
          personalizationErrorMessage,
          personalizationInput,
          isAddOnSelected,
          selectedInsurance,
          showCheckItsRightDialog,
          isDigitalDownloadDialogOpen,
          isAddToBasketDialogRendered,
          isAddToBasketDialogOpen,
          errorMessage,
          hasError,
          recsDialogRef,
          recsAtbRef,
          isFirstRender,
          isDigitalDownloadDialogRendered,
          filteredLinkedAddonServicesContent,
          isAddingToBasket,
        ],
      )}
    >
      {children}
    </SelectedSkuContext.Provider>
  );
}

export default SelectedSkuProvider;
